/**
 * Currency selection lives in localStorage so manual picks survive reloads, but
 * we also need to follow the language whenever the user switches it. The two
 * goals collide if we just always-sync on every page load (manual choice gets
 * wiped) OR if we never sync (language switch leaves stale currency).
 *
 * Solution mirrors the `plate_country` autosync pattern (see formModule.js):
 *
 *   - `currency`              — current pick (persisted, used by getters)
 *   - `autoSelectedCurrency`  — last value WE wrote automatically
 *   - `currencyAppliedLangId` — language id we last auto-applied for
 *
 * Manual override detection: if `currency` differs from `autoSelectedCurrency`,
 * the user picked it manually → never overwrite.
 * If they match (or no manual pick exists yet), the next language change is
 * free to retarget to lang.currency_code and update the mirror.
 *
 * `SET_CURRENCY` is reached only from the user-facing picker (SelectAppCurrency),
 * so it clears the mirror to mark the choice as manual.
 */

const STORAGE_KEY = 'currency';
const AUTO_SELECTED_CURRENCY_KEY = 'autoSelectedCurrency';
const CURRENCY_APPLIED_LANG_ID_KEY = 'currencyAppliedLangId';

function normalizeCurrencyCode(input) {
    if (input && typeof input === 'object') {
        return String(input.code ?? '').trim().toUpperCase() || null;
    }
    const code = String(input ?? '').trim().toUpperCase();
    return code || null;
}

function readStorage(key) {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
        return window.localStorage.getItem(key);
    } catch (_) {
        return null;
    }
}

function writeStorage(key, value) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        if (value == null || value === '') {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, String(value));
        }
    } catch (_) {
        /* private mode / quota */
    }
}

function hasManualCurrencyOverride() {
    const stored = normalizeCurrencyCode(readStorage(STORAGE_KEY));
    if (!stored) return false;
    const mirrored = normalizeCurrencyCode(readStorage(AUTO_SELECTED_CURRENCY_KEY));
    if (!mirrored) return true;
    return stored !== mirrored;
}

function resolveLangCurrencyCode(lang) {
    const fromLang = String(lang?.currency_code ?? '').trim().toUpperCase();
    return fromLang || null;
}

export const currencyModule = {
    namespaced: true,
    state: {
        selectedCurrencyCode: null
    },
    getters: {
        currentCurrency: (state, getters, rootState) => {
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const selectedCode = String(state.selectedCurrencyCode ?? '').trim().toUpperCase();
            const selected = list.find((item) => String(item?.code ?? '').trim().toUpperCase() === selectedCode);
            const fallback = selected || list[0] || {};

            return {
                ...fallback,
                code: String(fallback?.code ?? '').trim().toUpperCase(),
                symbol: String(fallback?.symbol ?? '').trim(),
                conversion: Number(fallback?.conversion ?? fallback?.rate ?? 1) || 1
            };
        }
    },
    mutations: {
        SET_CURRENCY(state, currency) {
            const code = normalizeCurrencyCode(currency);
            state.selectedCurrencyCode = code;

            writeStorage(STORAGE_KEY, code);
            /**
             * Reached from the manual picker (SelectAppCurrency.selectCurrency).
             * Clear the auto-mirror so this pick is treated as a manual override
             * by future language-driven syncs.
             */
            writeStorage(AUTO_SELECTED_CURRENCY_KEY, null);
            writeStorage(CURRENCY_APPLIED_LANG_ID_KEY, null);
        },
        SET_CURRENCY_CODE(state, code) {
            const normalizedCode = normalizeCurrencyCode(code);
            state.selectedCurrencyCode = normalizedCode;

            writeStorage(STORAGE_KEY, normalizedCode);
        },
        /**
         * Internal: write the auto-selected currency + which language id it was
         * applied for. Used by `applyLanguageCurrency` so subsequent visits can
         * detect that the user has not manually overridden us yet.
         */
        SET_AUTO_SELECTED(state, payload) {
            const code = normalizeCurrencyCode(payload?.code);
            const langId = payload?.langId ?? null;
            state.selectedCurrencyCode = code;
            writeStorage(STORAGE_KEY, code);
            writeStorage(AUTO_SELECTED_CURRENCY_KEY, code);
            writeStorage(
                CURRENCY_APPLIED_LANG_ID_KEY,
                langId == null || langId === '' ? null : String(langId)
            );
        }
    },
    actions: {
        /**
         * Boot path (runs from initState). Picks the currency in this order:
         *   1. Manual user pick (stored ≠ mirror) — keep it, do nothing else.
         *   2. Active language's `currency_code` — auto-apply and remember.
         *   3. Existing valid localStorage value (e.g. legacy session).
         *   4. First currency in the list.
         */
        initCurrency({ commit, rootState }) {
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const firstCode = normalizeCurrencyCode(list[0]);

            const isValidIn = (code) =>
                !!code && list.some((item) => normalizeCurrencyCode(item) === code);

            const storedCode = normalizeCurrencyCode(readStorage(STORAGE_KEY));
            const lang = rootState?.lang;
            const langCode = resolveLangCurrencyCode(lang);
            const langId = lang?.id ?? null;
            const mirroredCode = normalizeCurrencyCode(readStorage(AUTO_SELECTED_CURRENCY_KEY));
            const appliedLangIdRaw = readStorage(CURRENCY_APPLIED_LANG_ID_KEY);
            const appliedLangId = appliedLangIdRaw == null ? null : String(appliedLangIdRaw);
            const isManualPick = !!storedCode && storedCode !== mirroredCode;

            if (isManualPick && isValidIn(storedCode)) {
                commit('SET_CURRENCY_CODE', storedCode);
                return;
            }

            if (langCode && isValidIn(langCode)) {
                const langIdStr = langId == null ? null : String(langId);
                /**
                 * Skip the redundant write when the mirror already matches both
                 * the target currency and the active language id — avoids
                 * thrashing localStorage on every page load.
                 */
                if (storedCode === langCode && mirroredCode === langCode && appliedLangId === langIdStr) {
                    commit('SET_CURRENCY_CODE', langCode);
                    return;
                }
                commit('SET_AUTO_SELECTED', { code: langCode, langId });
                return;
            }

            if (isValidIn(storedCode)) {
                commit('SET_CURRENCY_CODE', storedCode);
                return;
            }

            commit('SET_CURRENCY_CODE', firstCode);
        },

        setCurrencyByCode({ commit, rootState }, code) {
            const targetCode = String(code ?? '').trim().toUpperCase();
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const firstCode = normalizeCurrencyCode(list[0]);
            const found = list.find((item) => normalizeCurrencyCode(item) === targetCode);
            commit('SET_CURRENCY_CODE', found ? targetCode : firstCode);
        },

        /**
         * Applies `lang.currency_code` to the currency, but only when the user
         * has not manually overridden the auto-selection. Called from the root
         * `syncCurrencyToLanguage` action and on bootstrap.
         */
        applyLanguageCurrency({ commit, rootState }, langArg) {
            const lang = langArg || rootState?.lang;
            const code = resolveLangCurrencyCode(lang);
            if (!code) return false;

            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const isValid = list.some((item) => normalizeCurrencyCode(item) === code);
            if (!isValid) return false;

            if (hasManualCurrencyOverride()) return false;

            commit('SET_AUTO_SELECTED', { code, langId: lang?.id ?? null });
            return true;
        },

        /** Reset both manual pick + autosync mirror (e.g. on form clear). */
        clearCurrencyOverride({ commit }) {
            writeStorage(STORAGE_KEY, null);
            writeStorage(AUTO_SELECTED_CURRENCY_KEY, null);
            writeStorage(CURRENCY_APPLIED_LANG_ID_KEY, null);
            commit('SET_CURRENCY_CODE', null);
        }
    }
};
